import { type GetServerSideProps } from 'next';
import React, { useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { type Locale } from '../../lib/types';
import { type CmsBoxDto } from '../../lib/types/cms/cms-box-dto';
import styles from '../../styles/ActivityGuide.module.css';
import api from '../../lib/utils/api';
import { mapActivityGuidePageData } from '../../lib/utils/mappers';
import { mapBoxData } from '../../lib/utils/mappers/map-box-data';
import AgeGroupSelector from '../../components/OurBoxes/AgeGroupSelector';
import cmsEndpoints from '../../lib/utils/cmsEndpoints';
import { useAppContext } from '../../context/shared';
import ToyDetails from '../../components/ActivityGuide/ToyDetails';
import { RichText } from '../../components/RichText';
import { PageHead } from '../../components/PageHead';

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  );

  const locale = context.locale as Locale;

  const [pageData, boxesData] = await Promise.all([
    api.get(cmsEndpoints.activityGuidePage()),
    api.get(cmsEndpoints.boxes()),
  ]);

  const page = mapActivityGuidePageData(
    pageData?.data?.data?.attributes,
    locale
  );

  const boxes = (boxesData?.data?.data || []).map((box: CmsBoxDto) =>
    mapBoxData(box, locale)
  );

  const ageGroups = boxes
    .map((box) => box.ageGroup)
    .filter((item) => item?.maxMonths);

  const sortedAgeGroups = ageGroups.sort((a, b) => a.maxMonths - b.maxMonths);

  const sortedBoxes = boxes
    .filter((item) => item?.ageGroup?.maxMonths)
    .sort((a, b) => a.ageGroup.maxMonths - b.ageGroup.maxMonths);

  return {
    props: {
      page,
      ageGroups: sortedAgeGroups,
      boxes: sortedBoxes,
    },
  };
};

function ActivityGuide(props: any) {
  const { localization } = useAppContext();

  const [currentAgeGroup, setCurrentAgeGroup] = useState(0);

  const emptyPage =
    props.ageGroups.length === 0 &&
    props.boxes.length === 0 &&
    !props.page.title &&
    !props.page.ageGroupSelectorTitle &&
    !props.page.description &&
    !props.page.boxItemToggle;

  if (emptyPage) return <></>;

  return (
    <>
      <PageHead title={props.page?.title} />
      <div className="bg-gray-1">
        <Container>
          <Row className="text-center pt-5">
            <Col>
              {props.page.title && <h1 className="mb-3">{props.page.title}</h1>}
              {props.page.ageGroupSelectorTitle && (
                <h6 className="fw-normal">
                  {props.page.ageGroupSelectorTitle}
                </h6>
              )}
            </Col>
          </Row>
          <Row className="pt-4">
            <Col>
              <AgeGroupSelector
                ageGroups={props.ageGroups}
                setCurrentAgeGroup={setCurrentAgeGroup}
                currentAgeGroup={currentAgeGroup}
              />
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col lg="10" xl="8">
              <div className="d-flex py-5 justify-content-center align-items-center">
                {props.boxes?.[currentAgeGroup]?.shopifyImageUrl && (
                  <div
                    className={[styles.boxIcon, 'me-3'].join(' ')}
                    style={{
                      backgroundImage: `url(${props.boxes[currentAgeGroup].shopifyImageUrl})`,
                    }}
                  />
                )}
                {props.boxes?.[currentAgeGroup]?.title && (
                  <div className={styles.pageSubtitle}>
                    {props.boxes[currentAgeGroup].title}
                  </div>
                )}
              </div>
              {props.page.description && (
                <RichText content={props.page.description} />
              )}
              <div
                className={[
                  styles.pageSubtitle,
                  'd-flex justify-content-center',
                ].join(' ')}
                style={{ paddingTop: '5rem', paddingBottom: '5rem' }}
              >
                {localization.activity_guide_toys_list_title}
              </div>
              {props.boxes?.[currentAgeGroup]?.boxItems.length > 0 && (
                <div
                  className={[
                    'd-flex flex-column gap-5',
                    styles.toysContainer,
                  ].join(' ')}
                >
                  {props.boxes[currentAgeGroup].boxItems.map(
                    (boxItem, index) => (
                      <ToyDetails
                        key={index}
                        title={boxItem.title}
                        fromAge={boxItem.fromAge}
                        description={boxItem.description}
                        image={boxItem.shopifyImageUrl}
                        footerText={props.page.boxItemToggle}
                        benefitTags={boxItem.benefitTags}
                        benefitLabelColors={props.page.benefitLabelColors}
                        howToUse={boxItem.howToUse}
                      />
                    )
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}

export default ActivityGuide;
